import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  TextField,
  InputAdornment,
  Divider,
  IconButton,
  Grid,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Stack,
  Collapse,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Receipt as ReceiptIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  CalendarToday as CalendarIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { Donation, PaymentMethod, DonationStatus } from '@/types';
import { apiService } from '@/services/api';
import { notificationService } from '@/services/notificationService';
import { appStateService } from '@/services/appStateService';
import { 
  AppLayout, 
} from '@/components/UI';

interface DonationsListProps {
  onNavigate?: (view: string) => void;
}

type FilterStatus = 'ALL' | 'PENDING' | 'CONFIRMED' | 'CANCELLED' | 'REFUNDED';
type SortBy = 'DATE_DESC' | 'DATE_ASC' | 'AMOUNT_DESC' | 'AMOUNT_ASC';

const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  CASH: 'Contanti',
  CARD: 'Carta',
  BANK_TRANSFER: 'Bonifico',
  CHECK: 'Assegno',
  OTHER: 'Altro',
};

const STATUS_LABELS: Record<DonationStatus, string> = {
  PENDING: 'In Attesa',
  CONFIRMED: 'Confermata',
  CANCELLED: 'Annullata',
  REFUNDED: 'Rimborsata',
};

export const DonationsList: React.FC<DonationsListProps> = ({ onNavigate }) => {
  const [donations, setDonations] = useState<Donation[]>([]);
  const [filteredDonations, setFilteredDonations] = useState<Donation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('ALL');
  const [sortBy, setSortBy] = useState<SortBy>('DATE_DESC');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    loadDonations();
  }, []);

  useEffect(() => {
    applyFiltersAndSort();
  }, [donations, searchTerm, filterStatus, sortBy, dateFrom, dateTo]);

  const loadDonations = async () => {
    setIsLoading(true);
    try {
      console.log('🔄 Loading donations...');
      
      const appState = appStateService.getState();
      if (!appState.user?.id) {
        throw new Error('User ID not available');
      }
      
      const donationsData = await apiService.getCollectorDonations(appState.user.id);
      
      console.log('✅ Donations loaded:', donationsData);
      
      // getCollectorDonations restituisce un oggetto PaginatedResponse
      setDonations(donationsData.data || donationsData);
    } catch (error: any) {
      console.error('❌ Error loading donations:', error);
      console.error('❌ Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      
      // Gestione errori specifica
      if (error.response?.status === 400) {
        notificationService.error('Errore', 'Richiesta non valida. Verifica i permessi utente.');
      } else if (error.response?.status === 401) {
        notificationService.error('Errore', 'Sessione scaduta. Effettua nuovamente il login.');
      } else if (error.response?.status === 403) {
        notificationService.error('Errore', 'Non hai i permessi per visualizzare le donazioni.');
      } else if (error.response?.status === 404) {
        notificationService.error('Errore', 'Endpoint non trovato. Verifica la configurazione API.');
      } else {
        notificationService.error('Errore', 'Impossibile caricare le donazioni. Verifica la connessione.');
      }
      
      // Fallback: mostra dati vuoti
      setDonations([]);
    } finally {
      setIsLoading(false);
    }
  };

  const applyFiltersAndSort = () => {
    let filtered = [...donations];

    // Filtro per status
    if (filterStatus !== 'ALL') {
      filtered = filtered.filter(donation => donation.status === filterStatus);
    }

    // Filtro per ricerca
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(donation =>
        donation.notes?.toLowerCase().includes(term) ||
        donation.paymentMethod.toLowerCase().includes(term) ||
        (Number(donation.amount) || 0).toString().includes(term) ||
        donation.donor?.firstName.toLowerCase().includes(term) ||
        donation.donor?.lastName.toLowerCase().includes(term) ||
        donation.donor?.company?.toLowerCase().includes(term)
      );
    }

    // Filtro per data
    if (dateFrom) {
      const fromDate = new Date(dateFrom);
      filtered = filtered.filter(donation => 
        new Date(donation.createdAt) >= fromDate
      );
    }

    if (dateTo) {
      const toDate = new Date(dateTo);
      toDate.setHours(23, 59, 59, 999); // Fine della giornata
      filtered = filtered.filter(donation => 
        new Date(donation.createdAt) <= toDate
      );
    }

    // Ordinamento
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'DATE_DESC':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case 'DATE_ASC':
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        case 'AMOUNT_DESC':
          return (Number(b.amount) || 0) - (Number(a.amount) || 0);
        case 'AMOUNT_ASC':
          return (Number(a.amount) || 0) - (Number(b.amount) || 0);
        default:
          return 0;
      }
    });

    setFilteredDonations(filtered);
  };

  const handleRefresh = async () => {
    await loadDonations();
    notificationService.success('Aggiornato', 'Lista donazioni aggiornata');
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterStatus('ALL');
    setDateFrom('');
    setDateTo('');
    setSortBy('DATE_DESC');
    setFiltersOpen(false);
  };

  const getStatusColor = (status: DonationStatus) => {
    switch (status) {
      case 'CONFIRMED':
        return 'success';
      case 'PENDING':
        return 'warning';
      case 'CANCELLED':
        return 'error';
      case 'REFUNDED':
        return 'info';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: DonationStatus) => {
    switch (status) {
      case 'CONFIRMED':
        return <CheckCircleIcon />;
      case 'PENDING':
        return <WarningIcon />;
      case 'CANCELLED':
        return <ErrorIcon />;
      case 'REFUNDED':
        return <RefreshIcon />;
      default:
        return <WarningIcon />;
    }
  };

  const getStatusLabel = (status: DonationStatus) => {
    return STATUS_LABELS[status] || status;
  };


  const totalAmount = filteredDonations.reduce((sum, donation) => sum + (Number(donation.amount) || 0), 0);

  if (isLoading) {
    return (
      <AppLayout
        title="Le Mie Donazioni"
        showSyncButton={false}
        currentView="donations-list"
        onNavigate={onNavigate}
      >
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="h6">Caricamento donazioni...</Typography>
        </Box>
      </AppLayout>
    );
  }

  return (
    <AppLayout
      title="Le Mie Donazioni"
      showSyncButton={false}
      currentView="donations-list"
      onNavigate={onNavigate}
    >
      <Box sx={{ p: 2 }}>
        {/* Filtri di Ricerca */}
        <Paper sx={{ mb: 3 }}>
          {/* Header dei filtri */}
          <Box sx={{ 
            p: 2, 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            cursor: 'pointer',
            '&:hover': { backgroundColor: 'action.hover' }
          }} onClick={() => setFiltersOpen(!filtersOpen)}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FilterIcon />
              <Typography variant="h6">
                Filtri di Ricerca
              </Typography>
              {(searchTerm || filterStatus !== 'ALL' || dateFrom || dateTo) && (
                <Chip 
                  label="Attivi" 
                  size="small" 
                  color="primary" 
                  variant="outlined"
                />
              )}
            </Box>
            <IconButton size="small">
              {filtersOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>

          {/* Contenuto dei filtri - Collassabile */}
          <Collapse in={filtersOpen}>
            <Box sx={{ p: 3, pt: 0 }}>
              <Stack spacing={3}>
                {/* Barra di ricerca */}
                <TextField
                  fullWidth
                  placeholder="Cerca per donatore, importo, note..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setSearchTerm('')} size="small">
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Filtri */}
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <InputLabel>Stato</InputLabel>
                      <Select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value as FilterStatus)}
                        input={<OutlinedInput label="Stato" />}
                      >
                        <MenuItem value="ALL">Tutte</MenuItem>
                        <MenuItem value="PENDING">In Attesa</MenuItem>
                        <MenuItem value="CONFIRMED">Confermate</MenuItem>
                        <MenuItem value="CANCELLED">Annullate</MenuItem>
                        <MenuItem value="REFUNDED">Rimborsate</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <InputLabel>Ordinamento</InputLabel>
                      <Select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value as SortBy)}
                        input={<OutlinedInput label="Ordinamento" />}
                      >
                        <MenuItem value="DATE_DESC">Data (Più Recente)</MenuItem>
                        <MenuItem value="DATE_ASC">Data (Più Vecchia)</MenuItem>
                        <MenuItem value="AMOUNT_DESC">Importo (Maggiore)</MenuItem>
                        <MenuItem value="AMOUNT_ASC">Importo (Minore)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="Data Da"
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="Data A"
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>

                {/* Azioni */}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    startIcon={<ClearIcon />}
                    onClick={clearFilters}
                    disabled={!searchTerm && filterStatus === 'ALL' && !dateFrom && !dateTo}
                  >
                    Pulisci Filtri
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<RefreshIcon />}
                    onClick={handleRefresh}
                  >
                    Aggiorna
                  </Button>
                </Box>
              </Stack>
            </Box>
          </Collapse>
        </Paper>


        {/* Risultati della ricerca */}
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Risultati ({filteredDonations.length} donazioni)
            </Typography>
            {filteredDonations.length > 0 && (
              <Typography variant="body2" color="text.secondary">
                Totale: €{totalAmount.toFixed(2)}
              </Typography>
            )}
          </Box>

          {filteredDonations.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <ReceiptIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                {donations.length === 0 ? 'Nessuna donazione' : 'Nessuna donazione trovata'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {donations.length === 0 
                  ? 'Inizia creando la tua prima donazione'
                  : 'Prova a modificare i filtri di ricerca'
                }
              </Typography>
            </Box>
          ) : (
            <Box sx={{ 
              maxHeight: '60vh', 
              overflowY: 'auto',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1
            }}>
              <Stack spacing={1} sx={{ p: 1 }}>
                {filteredDonations.map((donation) => (
                  <Card key={donation.id} sx={{ 
                    '&:hover': { 
                      boxShadow: 2,
                      transform: 'translateY(-1px)'
                    },
                    transition: 'all 0.2s ease-in-out'
                  }}>
                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Box>
                          <Typography variant="h6" component="div">
                            {donation.donor?.firstName} {donation.donor?.lastName}
                          </Typography>
                          {donation.donor?.company && (
                            <Typography variant="body2" color="text.secondary">
                              {donation.donor.company}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="h6" color="primary.main" fontWeight="bold">
                            €{(Number(donation.amount) || 0).toFixed(2)}
                          </Typography>
                          <Chip
                            label={getStatusLabel(donation.status)}
                            color={getStatusColor(donation.status) as any}
                            size="small"
                            icon={getStatusIcon(donation.status)}
                          />
                        </Box>
                      </Box>

                      <Divider sx={{ my: 1 }} />

                      <Grid container spacing={2}>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">
                            Data
                          </Typography>
                          <Typography variant="body2">
                            {new Date(donation.createdAt).toLocaleDateString('it-IT')}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">
                            Metodo Pagamento
                          </Typography>
                          <Typography variant="body2">
                            {PAYMENT_METHOD_LABELS[donation.paymentMethod]}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">
                            Ora
                          </Typography>
                          <Typography variant="body2">
                            {new Date(donation.createdAt).toLocaleTimeString('it-IT', { 
                              hour: '2-digit', 
                              minute: '2-digit' 
                            })}
                          </Typography>
                        </Grid>
                      </Grid>

                      {donation.notes && (
                        <>
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="caption" color="text.secondary">
                            Note
                          </Typography>
                          <Typography variant="body2">
                            {donation.notes}
                          </Typography>
                        </>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Box>
          )}
        </Paper>


      </Box>
    </AppLayout>
  );
};
